import * as React from 'react';
import { useCallback, useContext, useLayoutEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { AuthContext } from '../_auth/AuthContext';
import { Typography, TextField, Select, MenuItem, FormControl, FormControlLabel, InputLabel } from '@material-ui/core';
import { IInterface, IEquipmentManifest, IInterfaceWashEquipment, IInterfaceWashEquipmentPlan, ILocationServicePlan } from '../_types/location';
import { makeStyles } from '@material-ui/core/styles';
import Switch from '@material-ui/core/Switch';
import { Grid } from '@material-ui/core';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import InterfaceConfig from './_interface_config';
import TestRemoteStart from './_test_remote_start';


interface Props {
    classes: any;
    /** the item to display */
    item: IInterfaceWashEquipment;
    manifest: IEquipmentManifest;

    onCancel: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    onSave: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    onChange: (ifaceequip:IInterfaceWashEquipment) => void;
};

type PlanEditField = 'relay' | 'minutes' | 'deleted';

// Pass scalar values because the existing editor mutates plan objects in place.
const PlanFields = React.memo((props: {
    planUuid: string;
    relayName: string;
    minutes: number;
    deleted: boolean;
    relayLabel: string;
    minutesLabel: string;
    minutesHighlightClass: string;
    highlightClass: string;
    onEdit: (planUuid: string, field: PlanEditField, value: string | boolean) => void;
    onFieldFocus: (planUuid: string, field: 'relay' | 'minutes') => void;
}) => (
    <>
        <TableCell align="left">
            <TextField
                value={props.minutes}
                className={props.minutesHighlightClass}
                inputProps={{ 'aria-label': props.minutesLabel }}
                onFocus={() => props.onFieldFocus(props.planUuid, 'minutes')}
                onChange={e => props.onEdit(props.planUuid, 'minutes', e.target.value)}
            />
        </TableCell>
        <TableCell align="left">
            <TextField
                value={props.relayName}
                className={props.highlightClass}
                inputProps={{ 'aria-label': props.relayLabel }}
                onFocus={() => props.onFieldFocus(props.planUuid, 'relay')}
                onChange={e => props.onEdit(props.planUuid, 'relay', e.target.value)}
            />
        </TableCell>
        <TableCell align="left">
            <Switch checked={props.deleted} onChange={e => props.onEdit(props.planUuid, 'deleted', e.target.checked)} color="primary" />
        </TableCell>
    </>
));

// LEARN
const useStyles = makeStyles((theme) => ({
    formControl: {
        margin: theme.spacing(1),
        minWidth: 120,
        },
    deletedPlan: {
        textDecoration: "line-through"
    },
    table: {
        minWidth: 400,
    },
    matchingRelay: {
        outline: `2px solid ${theme.palette.primary.main}`,
        borderRadius: theme.shape.borderRadius,
    },
//     home: { color: "primary" },
//     away: { color: theme.palette.grey[900] }
}));

const EquipmentInterface: React.FC<Props> = (prop: Props) => {

    const classes = useStyles();
    const { user } = useContext(AuthContext);
    const [showDeletedPlans, setShowDeletedPlans] = useState(false);
    const [focusedRelayPlan, setFocusedRelayPlan] = useState<ILocationServicePlan | null>(null);
    const [mirrorMatchingRelays, setMirrorMatchingRelays] = useState(false);
    const [focusedMinutesPlan, setFocusedMinutesPlan] = useState<ILocationServicePlan | null>(null);
    const [mirrorMatchingMinutes, setMirrorMatchingMinutes] = useState(false);

    const _isHome = (plan: ILocationServicePlan): boolean =>
        plan.service_location_uuid === user.currentLocation?.uuid;

    const _matchesHomePlan = (plan: ILocationServicePlan, homePlan: ILocationServicePlan): boolean =>
        _isHome(homePlan) && !_isHome(plan) &&
        (plan.price === homePlan.price || plan.service_plan_name === homePlan.service_plan_name);

    const _MyInterface = (): IInterface => {
        let toReturn = prop.manifest.interfaces.find(g => g.editing);
        return toReturn as IInterface;
    }


    // Rebuild indexes to include in-place updates from the parent without repeated array scans.
    const plansByUuid = new Map(prop.manifest.plans.map(plan => [plan.service_plan_uuid, plan]));
    const equipmentPlansByUuid = new Map(prop.item.plans.map(plan => [plan.service_plan_uuid, plan]));
    const _GetPlan = (planUuid: string) => plansByUuid.get(planUuid);

    const _GetIWEP = (plan_uuid: string): IInterfaceWashEquipmentPlan => {
        let myPlan: IInterfaceWashEquipmentPlan | undefined = equipmentPlansByUuid.get(plan_uuid);
        let planToAdd = _GetPlan(plan_uuid);
        if (!myPlan) {
            
            let plan_id: number = 0;
            if (planToAdd) {
                plan_id = planToAdd.service_plan_id;
            }

            myPlan = {
                count_type_cn: "counttype.na",
                deleted: planToAdd?.deleted || false,
                fail_message: "",
                service_plan_uuid: plan_uuid,
                id: 0,
                interface_relay_name: "",
                interfacewashequipment_id: prop.item.id || 0,
                price: 0,
                service_plan_id: plan_id,
                success_message: ""

            };
            prop.item.plans.push(myPlan);
            equipmentPlansByUuid.set(plan_uuid, myPlan);
        }

        //myPlan.deleted = planToAdd?.deleted || false;

        return myPlan;
    }
    


    const editPlan = (planUuid: string, field: PlanEditField, value: string | boolean) => {
        const equipmentPlan = _GetIWEP(planUuid);
        const applyValue = (plan: IInterfaceWashEquipmentPlan) => {
            if (field === 'relay') {
                plan.interface_relay_name = value as string;
            } else if (field === 'minutes') {
                plan.price = +value;
            } else {
                plan.deleted = value as boolean;
            }
        };
        applyValue(equipmentPlan);
        const mirror = field === 'relay' ? mirrorMatchingRelays : field === 'minutes' && mirrorMatchingMinutes;
        const row = _GetPlan(planUuid);
        if (mirror && row && _isHome(row)) {
            prop.manifest.plans.forEach(plan => {
                if (_matchesHomePlan(plan, row)) {
                    applyValue(_GetIWEP(plan.service_plan_uuid));
                }
            });
        }
        prop.onChange(prop.item);
    };
    const focusField = (planUuid: string, field: 'relay' | 'minutes') => {
        const plan = _GetPlan(planUuid);
        if (plan && _isHome(plan)) {
            if (field === 'relay') {
                setFocusedRelayPlan(plan);
            } else {
                setFocusedMinutesPlan(plan);
            }
        }
    };

    // Stable handlers let unchanged fields skip rendering while using the latest parent data.
    const handlers = useRef({ editPlan, focusField });
    useLayoutEffect(() => { handlers.current = { editPlan, focusField }; });
    const onPlanEdit = useCallback((planUuid: string, field: PlanEditField, value: string | boolean) =>
        handlers.current.editPlan(planUuid, field, value), []);
    const onFieldFocus = useCallback((planUuid: string, field: 'relay' | 'minutes') =>
        handlers.current.focusField(planUuid, field), []);

    const _isRemote = ():boolean => {
        let trigger = _MyInterface().triggers.find(g => g.trigger_cn === "interfacetrigger.remotescan");
        
        return (trigger !== undefined) && !trigger.delete;
        
    }
    const _isScanner = ():boolean => {
        let trigger = _MyInterface().triggers.find(g => g.trigger_cn === "interfacetrigger.scanner");
        return (trigger !== undefined) && !trigger.delete;
    }
    const _isRemoteScanOrKeyPad = ():boolean => {
        let trigger = _MyInterface().triggers.find(g => g.trigger_cn === "interfacetrigger.finger" || g.trigger_cn === "interfacetrigger.scanner");
        return (trigger !== undefined) && !trigger.delete;
    }
    const _testRemoteStart = (service_location_uuid: string, service_plan_uuid: string) => {

        let userRole = null;
        
        let currentLocationId = user.currentLocation?.uuid;

        if (user.role) {
            userRole = user.role;
        }
        let showButton = _isRemote() && userRole === 4 && prop.item.id && currentLocationId === service_location_uuid;
        let iwep = _GetIWEP(service_plan_uuid);
        if (iwep.deleted) { showButton = false; }
        return showButton ? (
            <TableCell align="center">
                <TestRemoteStart manifest={prop.manifest} equipment_id={prop.item.qrcode} service_location_uuid={service_location_uuid} service_plan_uuid={service_plan_uuid}/>
            </TableCell>
          ): (
              <TableCell align="left">
                  
              </TableCell>
          ) 

    }

    return (
        <Grid container direction="column">
            {/* 
                add vs edit
                    equipment
                show relay name only if remote
                show qr if remote
                don't show plans if attendant
             */}
            <Grid item>
            {_MyInterface().id === 0 || prop.item.id === 0 ? (
                <FormControl className={prop.classes.formControl}>
                    <InputLabel id="equip-select-label">Equipment</InputLabel>
                    <Select
                    labelId="equip-select-label"
                    id="equip-select"
                    value={prop.item.washequipment_id}
                    onChange={(e) => {prop.item.washequipment_id = e.target.value as number; prop.onChange(prop.item)}}
                    >
                    {prop.manifest.equipment.map(e => {
                        let already = false;
                        _MyInterface().equipment.forEach(g => already = already || g.washequipment_id === e.id);
                        
                        return (!already || e.id === prop.item.washequipment_id) && (
                            <MenuItem value={e.id as number}>{e.name}</MenuItem>
                        )
                    })}
                

                    </Select>
                </FormControl>
            ) : (
                <Typography>{prop.manifest.equipment.find(g => g.id === prop.item.washequipment_id)?.name}</Typography>
            )}
            </Grid>
            {(_isRemote() || _isRemoteScanOrKeyPad()) && (
                <Grid item>
                    <Grid item>
                    <FormControl className={prop.classes.formControl}>
                        <TextField style={{ paddingRight: 5 }} label="QR Code" value={prop.item.qrcode} onChange={(e) => {let i = prop.item; i.qrcode = e.target.value; prop.onChange(i)}}></TextField>
                    </FormControl>
                    <FormControl className={prop.classes.formControl}>
                        <TextField label="Text Code" value={prop.item.text_code} onChange={(e) => {let i = prop.item; i.text_code = e.target.value; prop.onChange(i)}}></TextField>
                    </FormControl>
                    </Grid>
                </Grid>
            )}

            
            <Grid item>
                <InterfaceConfig classes={classes} manifest={prop.manifest} item={prop.item} onChange={(e)=>{prop.onChange(e)}}/>
            </Grid>
            

            {/* Plans */}
            { (_isRemote() || _isScanner()) && ( 
            <Grid item>
                <TableContainer component={Paper}>
                    <Table className={classes.table} size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell align="left">Location</TableCell>
                                <TableCell align="left">
                                    Plan
                                    <FormControlLabel
                                        label="Show deleted plans"
                                        control={
                                            <Switch
                                                checked={showDeletedPlans}
                                                onChange={(e) => setShowDeletedPlans(e.target.checked)}
                                                color="primary"
                                                size="small"
                                            />
                                        }
                                    />
                                </TableCell>
                                <TableCell align="left">Price</TableCell>
                                <TableCell align="left">
                                    Custom Minutes
                                    <FormControlLabel
                                        label="Mirror matching minutes"
                                        control={
                                            <Switch
                                                checked={mirrorMatchingMinutes}
                                                onChange={(e) => setMirrorMatchingMinutes(e.target.checked)}
                                                color="primary"
                                                size="small"
                                            />
                                        }
                                    />
                                </TableCell>
                                <TableCell align="left">
                                    Relay Name
                                    <FormControlLabel
                                        label="Mirror matching relays"
                                        control={
                                            <Switch
                                                checked={mirrorMatchingRelays}
                                                onChange={(e) => setMirrorMatchingRelays(e.target.checked)}
                                                color="primary"
                                                size="small"
                                            />
                                        }
                                    />
                                </TableCell>
                                <TableCell align="left">Not Permitted</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {prop.manifest.plans
                                .filter(row => showDeletedPlans || !row.deleted)
                                .sort((a, b) => Number(_isHome(b)) - Number(_isHome(a)) ||
                                    a.location_name.localeCompare(b.location_name) || a.price - b.price)
                                .map(row => (
                                <TableRow key={row.service_plan_id}>
                                    <TableCell align="left">{row.location_name}</TableCell>
                                    <TableCell align="left" className={clsx((_GetIWEP(row.service_plan_uuid).deleted || _GetPlan(row.service_plan_uuid)?.deleted) && classes.deletedPlan)}>
                                        ({row.service_plan_id})-{row.service_plan_name}
                                        { (_GetPlan(row.service_plan_uuid)?.deleted) && (<span>**deleted</span>) }
                                    </TableCell>
                                    <TableCell align="left">{row.price}</TableCell>
                                    <PlanFields
                                        planUuid={row.service_plan_uuid}
                                        minutes={_GetIWEP(row.service_plan_uuid).price}
                                        relayName={_GetIWEP(row.service_plan_uuid).interface_relay_name}
                                        deleted={_GetIWEP(row.service_plan_uuid).deleted}
                                        relayLabel={`Relay Name: ${row.location_name} - ${row.service_plan_name}`}
                                        highlightClass={clsx(focusedRelayPlan && _matchesHomePlan(row, focusedRelayPlan) && classes.matchingRelay)}
                                        onEdit={onPlanEdit}
                                        minutesLabel={`Custom Minutes: ${row.location_name} - ${row.service_plan_name}`}
                                        minutesHighlightClass={clsx(focusedMinutesPlan && _matchesHomePlan(row, focusedMinutesPlan) && classes.matchingRelay)}
                                        onFieldFocus={onFieldFocus}
                                    />
                                    {_testRemoteStart(row.service_location_uuid, row.service_plan_uuid)}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Grid>)}
        </Grid>
            
    )
}

export default EquipmentInterface;
